import VueAccessor from "../app/VueAccessor";
import {isNOU} from "../expansions/condition";
import {dateHelper} from "@/assets/plugins/mps-common/time/date-helper";
import {PATH} from "@/constants";
import {permissionCanonicalNameManager} from "@/assets/plugins/mps-common/user/permission-canonical-name-manager";
import {pref} from "@/assets/plugins/mps-common/preferences/preferences";
import PreferencesKeys from "@/assets/plugins/mps-common/preferences/key";
import routerManager from "@/assets/plugins/mps-common/router/router-manager";

/**
 * MPS USER 를 위한 관리자 입니다.
 * 로그인 정보, 유저 정보, 및 API 접근 토큰 관리자입니다.
 * 어플리케이션 내에서 한개만 유지되어야 합니다.
 */
class UserManager extends VueAccessor {

    _accessToken;
    _firebaseToken;
    _user = {
        countryCode: undefined,
        languageCode: undefined,
        canonicalNameList: [],
        adminUserId: undefined,
        displayName: undefined,
    };
    _permissionCanonicalNameManager;
    _sort;

    constructor() {
        super();
        this._permissionCanonicalNameManager = permissionCanonicalNameManager;
        try {
            this._restorePersistenceData(); // 유저 데이터 복구.
        } catch (e) {
            this.logout();
        }
    }

    /**
     * 영속 데이터를 복구합니다.
     * @private
     */
    _restorePersistenceData() {
        this.accessToken = pref.get(PreferencesKeys.KEY_ACCESS_TOKEN, null);
        this.user = pref.get(PreferencesKeys.KEY_USER, null);
        this.firebaseToken = pref.get(PreferencesKeys.FCM_TOKEN, '');
        // 사용자 로그인 상태면 권한을 복구해서 권한 매니저에 설정합니다.
        if (!!this.user) {
            // 권한이 있습니다. 복구.
            this._permissionCanonicalNameManager.setPermissions(this.user.canonicalNameList);
        }
        // 사용자 로그인 상태가 아니면
    }

    set accessToken(accessToken) {
        this._accessToken = accessToken;
        pref.put(PreferencesKeys.KEY_ACCESS_TOKEN, accessToken);
    }

    get accessToken() {
        return this._accessToken;
    }

    get userType() {
        return this._sort;
    }

    get firebaseToken() {
        return this._firebaseToken;
    }

    set firebaseToken(value) {
        this._firebaseToken = value;
    }

    set user(user) {
        this._user = user;
        pref.put(PreferencesKeys.KEY_USER, user);
        if (!!user) {
            dateHelper.setUser(user);
        }
    }

    get locale() {
        return (this._user.languageCode||"").toLowerCase() + "-" + (this._user.countryCode||"").toUpperCase();
    }

    get user() {
        return this._user;
    }

    get userName() {
        return this._user.displayName;
    }

    get hasAccessToken() {
        return this.accessToken !== null;
    }

    get authorization() {
        return this.hasAccessToken ? 'Bearer ' + this.accessToken : null;
    }

    get validate() {
        return !(isNOU(this.accessToken) || isNOU(this.user));
    }

    hasPermission(canonicalPermissionName) {
        return this._permissionCanonicalNameManager.hasPermission(canonicalPermissionName);
    }

    login(loginId, password) {
        const request = this.vm.$requestWithOverlay(PATH.AUTH.LOGIN)
            .setObject({
                loginId: loginId.trim(),
                password: password.trim()
            });
        return new Promise(async (resolve, reject) => {
            try {
                const response = await request.enqueue();
                this.accessToken = response.accessToken;
                this.user = response.user;
                this._permissionCanonicalNameManager.setPermissions(this.user.canonicalNameList); // 사용자 권한
                this.vm.$translationManager.save(response.languages);
                resolve(response);
            } catch (e) {
                console.log(e);
                reject(e);
            }
        });
    }

    logout() {
        this.accessToken = null;
        this.user = null;
        routerManager.push({path: "/"});
    }
}

const userManager = new UserManager();

export default userManager;
